import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ExternalLink, Loader2, Sparkles } from "lucide-react";

const EMOJIS = [
  { rating: 1, emoji: "😞", label: "Bad" },
  { rating: 2, emoji: "😕", label: "Poor" },
  { rating: 3, emoji: "😐", label: "Okay" },
  { rating: 4, emoji: "🙂", label: "Good" },
  { rating: 5, emoji: "😄", label: "Great" },
];

const DISMISS_KEY_PREFIX = "efgh_review_dismiss_";

/**
 * Emoji-based review wizard shown on the Smart Report page after publish.
 * - Auto-opens once per report (unless previously dismissed / submitted).
 * - If patient rates ≥ redirect_threshold and the effective program has an
 *   `external_url` configured, the front-end redirects them there.
 * - Otherwise stays internal.
 *
 * The **effective program** comes from the backend:
 *   • For lab-attached reports → the lab's Review Program (their GMB, etc.).
 *   • For patient-uploaded reports (no lab) → the EFGH platform's Review Program.
 * So a single rating flow serves both destinations transparently.
 */
export default function ReviewWizard({ reportId, autoOpen = true }) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!reportId) return;
    api.get(`/reports/${reportId}/review-config`)
      .then((r) => {
        setConfig(r.data);
        if (r.data.existing) {
          setRating(r.data.existing.rating);
          setFeedback(r.data.existing.feedback || "");
          setSubmitted(true);
        }
        const dismissed = localStorage.getItem(DISMISS_KEY_PREFIX + reportId);
        if (autoOpen && r.data.eligible && r.data.program?.enabled && !r.data.existing && !dismissed) {
          setTimeout(() => setOpen(true), 2500);
        }
      })
      .catch(() => {});
  }, [reportId, autoOpen]);

  const submit = async () => {
    if (!rating) return toast.error("Pick a rating first");
    setSubmitting(true);
    try {
      const r = await api.post(`/reports/${reportId}/review`, { rating, feedback: feedback.trim() || null });
      setSubmitted(true);
      if (r.data.redirect_external && r.data.external_url) {
        toast.success("Thanks! Redirecting to leave a public review…");
        setTimeout(() => window.open(r.data.external_url, "_blank", "noopener"), 800);
      } else {
        toast.success(r.data.thank_you_message || "Thank you for your feedback!");
        setTimeout(() => setOpen(false), 1400);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not submit review"));
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY_PREFIX + reportId, "1");
    setOpen(false);
  };

  if (!config?.program?.enabled) return null;

  const entityName = config.program?.lab_name || "EFGH";

  return (
    <>
      {/* Floating trigger — always visible if configured */}
      {config.eligible && !open && (
        <button
          type="button"
          data-testid="review-wizard-trigger"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-30 rounded-full bg-white border border-border shadow-lg pl-3 pr-4 py-2 flex items-center gap-2 hover:bg-muted transition-all print:hidden"
        >
          <Sparkles className="w-4 h-4 text-efgh-yellow" />
          <span className="text-sm font-medium text-ink">{submitted ? "Update rating" : "Rate this report"}</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="review-wizard-dialog">
          <DialogTitle className="font-heading text-lg text-ink">
            {submitted ? "Update your rating" : `How was your experience with ${entityName}?`}
          </DialogTitle>
          <p className="text-sm text-ink/60">Your feedback helps us improve.</p>

          <div className="mt-6 flex justify-between items-end gap-1" data-testid="review-emoji-row">
            {EMOJIS.map((e) => {
              const active = rating === e.rating;
              return (
                <button
                  key={e.rating}
                  type="button"
                  data-testid={`review-emoji-${e.rating}`}
                  onClick={() => setRating(e.rating)}
                  className={`flex flex-col items-center gap-1 rounded-xl px-1 sm:px-2 py-2 transition-all flex-1 min-w-0 ${
                    active ? "bg-efgh-yellow/20 scale-105 sm:scale-110" : "hover:bg-muted"
                  }`}
                >
                  <span
                    className={`text-3xl sm:text-4xl transition-transform ${active ? "scale-110 sm:scale-125" : "grayscale opacity-70 hover:opacity-100 hover:grayscale-0"}`}
                    style={{ lineHeight: 1 }}
                  >
                    {e.emoji}
                  </span>
                  <span className={`text-[9px] sm:text-[10px] uppercase tracking-wider truncate max-w-full ${active ? "text-ink font-semibold" : "text-ink/50"}`}>
                    {e.label}
                  </span>
                </button>
              );
            })}
          </div>

          {rating > 0 && (
            <div className="mt-6 space-y-3">
              {rating >= (config.program.redirect_threshold || 4) && config.program.external_url ? (
                <div className="p-3 rounded-xl bg-efgh-green/10 border border-efgh-green/30 text-sm text-ink flex items-start gap-2">
                  <ExternalLink className="w-4 h-4 mt-0.5 shrink-0 text-efgh-green" />
                  <div>
                    <div className="font-medium">Would you share your rating publicly?</div>
                    <div className="text-ink/60 text-xs mt-0.5">Submitting will open {entityName}&apos;s review page in a new tab.</div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/50 font-heading font-semibold">Tell us more (optional)</label>
                  <Textarea
                    data-testid="review-feedback"
                    rows={3}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder={rating <= 2 ? "What went wrong? We'll pass this on." : "What did you like?"}
                    maxLength={2000}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <Button type="button" data-testid="review-dismiss" variant="ghost" onClick={dismiss} className="text-ink/60 order-2 sm:order-1">
              Remind me later
            </Button>
            <Button
              type="button"
              data-testid="review-submit"
              disabled={!rating || submitting}
              onClick={submit}
              className="bg-ink hover:bg-ink/90 text-white order-1 sm:order-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {rating >= (config.program.redirect_threshold || 4) && config.program.external_url ? "Submit & share" : "Submit rating"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
